import React from 'react';
import classnames from 'classnames';
import { createUseStyles, useTheme } from 'react-jss';
import { Colors, Theme } from '@app/theme';
import { SessionStatus } from '@app/api';
import { Styles } from 'jss';
import mapValues from 'lodash/mapValues';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    ...theme.fonts.label,
  },
  onelineContainer: {
    display: 'flex',
    alignItems: 'center',
    ...theme.fonts.h4,
  },
  icon: {
    fontSize: theme.iconSize.regular,
  },
  textLine: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  textLineOneline: {
    width: 'auto',
    marginRight: theme.marginBase * 2,
  },
  progressBar: {
    marginTop: 4,
    width: theme.marginBase * 9,
    flex: 1,
    height: 4,
    borderRadius: theme.borderRadius.std,
    backgroundColor: theme.colors.grayBorder,
    overflow: 'hidden',
  },
  progress: {
    backgroundColor: theme.colors.inkSecondary,
    height: 4,
  },
  text: {
    ...theme.fonts.h3,
  },
}));

const useColorStyles = createUseStyles((theme: Theme): Styles<Colors> => {
  return mapValues(theme.colorDefinitions, (def) => ({
    color: theme.colors.inkSecondary,
    '& i': {
      color: theme.colors.white,
    },
    '& span': {
      color: theme.colors.white,
    },
    '& .progress': {
      backgroundColor: def.accessible,
    },
    '& .progressBack': {
      backgroundColor: theme.colors.white,
    },
  }));
});

const usePlainStyles = createUseStyles((theme: Theme): Styles<Colors> => {
  return mapValues(theme.colorDefinitions, (def) => ({
    color: def.std,
    '& i': {
      color: def.std,
    },
    '& span': {
      color: def.accessible,
    },
    '& .progress': {
      backgroundColor: def.muted,
    },
    '& .progressBack': {
      backgroundColor: def.std,
    },
  }));
});

interface Props {
  current: number;
  max: number;
  color?: Colors;
  oneline?: boolean;
  displayType?: 'percent' | 'fraction';
  className?: string;
  variant?: 'white' | 'plain';
  strong?: boolean;
}

export const ProgressLine = (props: Props) => {
  const {
    current,
    max = 100,
    oneline,
    strong = false,
    displayType = 'fraction',
    color = 'blue',
    variant = 'plain',
    className,
  } = props;
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const colorClasses = useColorStyles({ theme });
  const colorPlain = usePlainStyles({ theme });

  let text = max ? `${current} / ${max}` : '- / -';
  if (displayType === 'percent') {
    text = Math.trunc((current / max) * 100).toString() + '%';
  }

  return (
    <div
      className={classnames(
        classes.container,
        {
          [colorClasses[color]]: variant === 'white',
          [colorPlain[color]]: variant === 'plain',
          [classes.onelineContainer]: oneline,
        },
        className,
      )}
    >
      <div
        className={classnames(classes.textLine, {
          [classes.textLineOneline]: oneline,
        })}
      >
        {!oneline && (
          <i className={classnames(classes.icon, 'ri-user-3-line')} />
        )}
        <span className={classnames({ [classes.text]: strong })}>{text}</span>
      </div>
      <div className={classnames(classes.progressBar, 'progress')}>
        <div
          className={classnames(classes.progress, 'progressBack')}
          style={{ width: max ? `${(100 * current) / max}%` : 0 }}
        />
      </div>
    </div>
  );
};
